import React, { useEffect, useState } from "react";
import AccountLayout from "../../account/AccountLayout";
import { fetchPreferences, patchPreferences } from "../../account/accountApiClient";
import { AccountButton, AccountCard, AccountStatus, ToggleRow } from "../../account/AccountUi";
import { captureScope, isCurrentScope } from "../../auth/authSession";

const PAPER_FIELDS = [
  { key: "pushEnabled", label: "Push notifications" },
  { key: "fills", label: "Fills" },
  { key: "riskBlocks", label: "Risk blocks" },
  { key: "errors", label: "Errors" },
  { key: "lateSignals", label: "Late signals" },
  { key: "signalFlips", label: "Signal flips" },
  { key: "quoteStale", label: "Stale quotes" },
  { key: "dailySummary", label: "Daily summary" },
];

const EVENT_FIELDS = [
  { key: "companyAlertsEnabled", label: "Company alerts" },
  { key: "marketAlertsEnabled", label: "Market alerts" },
  { key: "billingSecurityAlertsEnabled", label: "Billing and security alerts" },
  { key: "redactedPushPreviews", label: "Redact push previews" },
];

export default function PreferencesPage() {
  return (
    <AccountLayout
      title="Preferences"
      description="Notification categories shared with the Stratify iPhone app."
      path="/account/preferences"
    >
      <PreferencesBody />
    </AccountLayout>
  );
}

function PreferencesBody() {
  const [prefs, setPrefs] = useState(null);
  const [state, setState] = useState("idle");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [tone, setTone] = useState("info");

  async function load() {
    const scope = captureScope();
    setState("loading");
    try {
      const json = await fetchPreferences({ scope });
      if (!isCurrentScope(scope)) return;
      setPrefs(json.preferences);
      setState("ready");
    } catch (err) {
      if (!isCurrentScope(scope)) return;
      setMessage(err?.message || "Could not load preferences.");
      setTone("error");
      setState("error");
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function saveGroup(group) {
    if (!prefs) return;
    setBusy(true);
    setMessage(null);
    const scope = captureScope();
    const body =
      group === "paper"
        ? {
            paperBotNotifications: PAPER_FIELDS.reduce((acc, field) => {
              acc[field.key] = Boolean(prefs.paperBotNotifications?.[field.key]);
              return acc;
            }, {}),
            expectedPaperRevision: prefs.paperBotNotifications?.revision,
          }
        : {
            eventAlerts: EVENT_FIELDS.reduce((acc, field) => {
              acc[field.key] = Boolean(prefs.eventAlerts?.[field.key]);
              return acc;
            }, {}),
            expectedEventRevision: prefs.eventAlerts?.revision,
          };
    try {
      const json = await patchPreferences(body, { scope });
      if (!isCurrentScope(scope)) return;
      setPrefs(json.preferences);
      setTone("info");
      setMessage("Preferences saved. These apply to iPhone notifications.");
    } catch (err) {
      if (err?.code === "revision_conflict" && err.details?.preferences) {
        setPrefs(err.details.preferences);
        setTone("error");
        setMessage("Preferences were updated elsewhere. Reload and try again.");
      } else {
        setTone("error");
        setMessage(err?.message || "Could not save preferences.");
      }
    } finally {
      setBusy(false);
    }
  }

  function updatePaper(key, value) {
    setPrefs((prev) => ({
      ...prev,
      paperBotNotifications: { ...prev.paperBotNotifications, [key]: value },
    }));
  }

  function updateEvent(key, value) {
    setPrefs((prev) => ({
      ...prev,
      eventAlerts: { ...prev.eventAlerts, [key]: value },
    }));
  }

  return (
      <div className="space-y-5">
        {state === "loading" && !prefs ? <AccountStatus>Loading preferences…</AccountStatus> : null}
        {state === "error" && !prefs ? (
          <AccountCard>
            <AccountStatus tone="error">{message}</AccountStatus>
            <AccountButton variant="secondary" className="mt-3" onClick={load}>
              Try again
            </AccountButton>
          </AccountCard>
        ) : null}

        {prefs ? (
          <>
            <AccountCard>
              <h2 className="text-base font-semibold text-ink">Paper bot notifications</h2>
              <p className="mt-1 text-sm text-muted">Delivered on iPhone when push is enabled.</p>
              <div className="mt-3 divide-y divide-ink/5">
                {PAPER_FIELDS.map((field) => (
                  <ToggleRow
                    key={field.key}
                    id={`paper-${field.key}`}
                    label={field.label}
                    checked={prefs.paperBotNotifications?.[field.key]}
                    onChange={(value) => updatePaper(field.key, value)}
                    disabled={busy}
                  />
                ))}
              </div>
              <AccountButton className="mt-4" disabled={busy} onClick={() => saveGroup("paper")}>
                Save paper notifications
              </AccountButton>
            </AccountCard>

            <AccountCard>
              <h2 className="text-base font-semibold text-ink">Event alerts</h2>
              <div className="mt-3 divide-y divide-ink/5">
                {EVENT_FIELDS.map((field) => (
                  <ToggleRow
                    key={field.key}
                    id={`event-${field.key}`}
                    label={field.label}
                    checked={prefs.eventAlerts?.[field.key]}
                    onChange={(value) => updateEvent(field.key, value)}
                    disabled={busy}
                  />
                ))}
              </div>
              <AccountButton className="mt-4" disabled={busy} onClick={() => saveGroup("event")}>
                Save event alerts
              </AccountButton>
            </AccountCard>

            <AccountCard>
              <h2 className="text-base font-semibold text-ink">Display currency</h2>
              <p className="mt-2 text-sm text-muted">
                Display currency is stored only on the iPhone. It is not a cross-device account setting.
              </p>
            </AccountCard>
          </>
        ) : null}

        {message && prefs ? <AccountStatus tone={tone}>{message}</AccountStatus> : null}
      </div>
  );
}
